/**
 * The arithmetic of the waits for the model outside the landing (practice's first pack, a new pack, an exam, a
 * voice, a pronunciation check), DOM-free so a unit test can read it. The elements that draw them are in
 * `wait-bits.ts`. Nothing here claims progress: a caption says what is being made, never how far along it is.
 */

/** A skeleton stays hidden this long, so a cached answer never flashes one (the CSS delay in `.wait-late`). */
export const SKELETON_DELAY_MS = 200;
/** A stepped caption holds each line this long before the next fades in. */
export const CAPTION_STEP_MS = 3500;
/** The most skeleton cards a practice wait draws. */
export const SKELETON_CARDS = 4;

/**
 * Which of `n` caption lines shows after `elapsed` ms: one every `step`, holding on the last (the wait never
 * loops back, which would read as starting over). No lines gives -1.
 */
export function captionAt(elapsed: number, n: number, step = CAPTION_STEP_MS): number {
  if (n <= 0) return -1;
  if (!(elapsed > 0) || step <= 0) return 0;
  return Math.min(n - 1, Math.floor(elapsed / step));
}

/**
 * The words a practice wait shows on its skeleton cards: the moment's own words in the order given, blank and
 * repeated ones left out, at most `max`. They are faint chips of what the questions will ask, never a promise of
 * which card asks which.
 */
export function skeletonWords(words: readonly string[], max = SKELETON_CARDS): string[] {
  const out: string[] = [];
  const seen = new Set<string>();
  for (const w of words) {
    const word = w.trim();
    const key = word.toLocaleLowerCase();
    if (!word || seen.has(key)) continue;
    seen.add(key);
    out.push(word);
    if (out.length >= max) break;
  }
  return out;
}

/** What a play button shows: at rest, preparing (the voice asked for, not yet heard) or playing. */
export type PlayState = 'rest' | 'preparing' | 'playing';

/** A play button's state from what `onSpeaking` names (`speaking`, `heard`) and the text the button plays. */
export function playState(speaking: string | null, heard: boolean, text: string): PlayState {
  if (!text || speaking !== text) return 'rest';
  return heard ? 'playing' : 'preparing';
}

/**
 * The widths (a share of the card, 0–1) of an exam wait's `n` story lines: long lines with a shorter last one
 * per paragraph of `per` lines, so the skeleton reads as prose. Fixed, not random, so it never jumps on redraw.
 */
export function storyWidths(n: number, per = 4): number[] {
  const long = [0.96, 0.9, 0.93, 0.88, 0.95];
  return Array.from({ length: Math.max(0, n) }, (_, i) =>
    per > 0 && i % per === per - 1 ? 0.55 + (i % 3) * 0.08 : long[i % long.length]);
}
